// Složená pole formuláře: opakované řádky (telefony, e-maily, adresy…), štítky, obrázek z úložiště.
// Stejné rozhraní jako jednoduché vstupy, aby je FieldControl mohl jen delegovat.
import { h, clear } from '../dom';
import { t, tk } from '../i18n';
import { icon } from '../icons';
import { core, upload } from '../api';
import { pickFile } from '../shell/picker';
import { pickNative } from '../shell/dropzone';
import type { FieldDef, RowsColumn, StorageItem, LookupHit } from '../contract';
import { IwMenu } from './menu';
import { loadLabels, labelColor, type CoreLabel } from '../labels';

export interface ValueControl {
  el: HTMLElement;
  get(): unknown;
  set(v: unknown): void;
  setEdit(edit: boolean): void;
  focus(): void;
  onChange: (() => void) | null;
}

const prefix = (): string => (JSON.parse(document.getElementById('iwan-boot')?.textContent ?? '{}') as { prefix?: string }).prefix ?? '';

/** Opakované řádky: každý řádek = objekt {sloupec: hodnota}; prázdné řádky se neukládají. */
export class RowsControl implements ValueControl {
  el = h('div', { class: 'rows-field' });
  onChange: (() => void) | null = null;
  private rows: Record<string, string>[] = [];
  private edit = false;
  private body = h('div', { class: 'rows-body' });
  private addBtn = h('button', { type: 'button', class: 'btn ghost rows-add' }, icon('plus'), t('core.field.add_row'));

  constructor(private def: FieldDef) {
    this.el.append(this.body, this.addBtn);
    this.addBtn.addEventListener('click', () => {
      this.rows.push(this.blank());
      this.render();
      this.body.querySelector<HTMLElement>('.rows-row:last-child input, .rows-row:last-child select, .rows-row:last-child textarea')?.focus();
      this.onChange?.();
    });
  }

  private cols(): RowsColumn[] {
    return this.def.columns ?? [];
  }

  private blank(): Record<string, string> {
    const r: Record<string, string> = {};
    for (const c of this.cols()) r[c.key] = c.type === 'select' ? String(c.options?.[0]?.value ?? '') : '';
    return r;
  }

  get(): unknown {
    // řádek platí, když má vyplněný kterýkoli sloupec (i jen výběr – např. akce pravidla); prázdné řádky se vynechají
    const keys = this.cols().map((c) => c.key);
    return this.rows.filter((r) => keys.some((k) => (r[k] ?? '').trim() !== '')).map((r) => ({ ...r }));
  }

  set(v: unknown): void {
    this.rows = Array.isArray(v) ? (v as Record<string, unknown>[]).map((r) => Object.fromEntries(this.cols().map((c) => [c.key, r[c.key] === null || r[c.key] === undefined ? '' : String(r[c.key])]))) : [];
    this.render();
  }

  setEdit(edit: boolean): void {
    this.edit = edit;
    this.addBtn.hidden = !edit;
    this.render();
  }

  focus(): void {
    this.body.querySelector<HTMLElement>('input, select, textarea')?.focus();
  }

  private render(): void {
    clear(this.body);
    this.el.classList.toggle('edit', this.edit);
    if (!this.edit) {
      const shown = this.get() as Record<string, string>[];
      if (!shown.length) {
        this.body.appendChild(h('div', { class: 'field-view' }, '—'));
        return;
      }
      for (const r of shown) {
        const line = h('div', { class: 'rows-view-row' });
        for (const c of this.cols()) {
          const val = r[c.key] ?? '';
          if (!val) continue;
          if (c.type === 'select') {
            line.appendChild(h('span', { class: 'rows-type' }, tk(c.options?.find((o) => String(o.value) === val)?.label ?? val)));
          } else if (c.type === 'phone') {
            line.appendChild(h('a', { href: 'tel:' + val.replace(/[^+\d]/g, ''), class: 'rows-val' }, val));
          } else if (c.type === 'email') {
            line.appendChild(h('a', { href: 'mailto:' + val, class: 'rows-val' }, val));
          } else if (c.type === 'url') {
            line.appendChild(h('a', { href: /^https?:\/\//i.test(val) ? val : 'https://' + val, target: '_blank', rel: 'noopener', class: 'rows-val' }, val));
          } else {
            line.appendChild(h('span', { class: 'rows-val' }, val));
          }
        }
        this.body.appendChild(line);
      }
      return;
    }
    this.rows.forEach((r, i) => {
      const row = h('div', { class: 'rows-row' });
      for (const c of this.cols()) {
        let input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        if (c.type === 'select') {
          input = h('select', { class: 'input', title: tk(c.label) }, (c.options ?? []).map((o) => h('option', { value: String(o.value), selected: String(o.value) === r[c.key] }, tk(o.label))));
        } else if (c.type === 'textarea') {
          input = h('textarea', { class: 'input', rows: '2', placeholder: tk(c.label) }, r[c.key] ?? '');
        } else {
          input = h('input', { class: 'input', type: c.type === 'email' ? 'email' : c.type === 'phone' ? 'tel' : c.type === 'url' ? 'url' : 'text', placeholder: tk(c.label), value: r[c.key] ?? '', autocomplete: 'off' });
        }
        input.style.flex = String(c.width ?? (c.type === 'select' ? 0.6 : 1));
        if (c.lookup && input instanceof HTMLInputElement) this.wireLookup(input, c, r, row);
        input.addEventListener('input', () => {
          r[c.key] = input.value;
          this.onChange?.();
        });
        input.addEventListener('change', () => {
          r[c.key] = input.value;
          this.onChange?.();
        });
        row.appendChild(input);
      }
      const del = h('button', { type: 'button', class: 'btn ghost sm', title: t('core.field.remove_row') }, icon('x'));
      del.addEventListener('click', () => {
        this.rows.splice(i, 1);
        this.render();
        this.onChange?.();
      });
      row.appendChild(del);
      this.body.appendChild(row);
    });
  }

  /** Napovídání z jádra (`lookup.search`): datalist pod vstupem; výběr návrhu doplní i ostatní sloupce podle `fill`. */
  private wireLookup(input: HTMLInputElement, c: RowsColumn, r: Record<string, string>, row: HTMLElement): void {
    const listId = 'lk-' + Math.random().toString(36).slice(2, 8);
    const list = h('datalist', { id: listId });
    input.setAttribute('list', listId);
    row.appendChild(list);
    let hits: LookupHit[] = [];
    let seq = 0;
    const apply = (hit: LookupHit) => {
      for (const [col, field] of Object.entries(c.fill ?? {})) {
        const v = hit.fields[field];
        if (v === undefined) continue;
        r[col] = v;
        const idx = this.cols().findIndex((x) => x.key === col);
        const el = row.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea')[idx];
        if (el) el.value = v;
      }
      if (!c.fill?.[c.key]) r[c.key] = input.value;
      this.onChange?.();
    };
    const search = async () => {
      const q = input.value.trim();
      if (q.length < 2) return;
      const my = ++seq;
      try {
        const res = await core<{ hits: LookupHit[] }>('lookup', 'search', { source: c.lookup, q });
        if (my !== seq) return;
        hits = res.hits;
        list.replaceChildren(...hits.map((hh) => h('option', { value: hh.label }, hh.detail ?? '')));
      } catch {
        /* bez návrhů */
      }
    };
    input.addEventListener('input', () => {
      const hit = hits.find((hh) => hh.label === input.value);
      if (hit) apply(hit);
      else window.setTimeout(() => { if (input.value.trim().length >= 2) void search(); }, 250);
    });
    input.addEventListener('change', () => {
      const hit = hits.find((hh) => hh.label === input.value);
      if (hit) apply(hit);
    });
  }
}

/** Štítky: seznam krátkých textů; „+ Přidat štítek“ jako u ostatních seznamů, pole se ukáže po kliknutí (Enter/čárka přidá, Esc zavře). */
export class TagsControl implements ValueControl {
  el = h('div', { class: 'tags-field' });
  onChange: (() => void) | null = null;
  private tags: string[] = [];
  private chips = h('div', { class: 'tags-chips' });
  private input = h('input', { class: 'input tags-input', type: 'text', autocomplete: 'off', placeholder: t('core.field.add_tag') });
  private addBtn = h('button', { type: 'button', class: 'btn ghost rows-add' }, icon('plus'), t('core.field.add_tag'));
  /** potvrzení štítku (telefon nemusí mít na klávesnici Enter) */
  private okBtn = h('button', { type: 'button', class: 'btn primary tags-ok', title: t('core.ui.ok'), 'aria-label': t('core.ui.ok') }, icon('check'));
  private addRow = h('div', { class: 'tags-add' }, this.input, this.okBtn);
  private edit = false;
  private adding = false;

  /** společný seznam štítků jádra (def.labels): nabídka a barvy čipů */
  private catalog: CoreLabel[] = [];

  constructor(private def: FieldDef) {
    const listId = 'tags-' + Math.random().toString(36).slice(2, 8);
    this.input.setAttribute('list', listId);
    const datalist = h('datalist', { id: listId }, (def.suggest ?? []).map((s) => h('option', { value: s })));
    this.el.append(this.chips, this.addRow, this.addBtn, datalist);
    if (def.labels) {
      void loadLabels().then((list) => {
        this.catalog = list;
        datalist.replaceChildren(...list.map((l) => h('option', { value: l.name })));
        this.render();
      });
    }
    this.addBtn.addEventListener('click', () => this.setAdding(true));
    this.okBtn.addEventListener('pointerdown', (e) => e.preventDefault()); // pole nepřijde o fokus (blur by přidávání zavřel)
    this.okBtn.addEventListener('click', () => {
      this.commit();
      this.input.focus();
    });
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        this.commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.input.value = '';
        this.setAdding(false);
      } else if (e.key === 'Backspace' && !this.input.value && this.tags.length) {
        this.tags.pop();
        this.render();
        this.onChange?.();
      }
    });
    this.input.addEventListener('blur', () => {
      this.commit();
      this.setAdding(false);
    });
  }

  private setAdding(on: boolean): void {
    this.adding = on && this.edit;
    this.addRow.hidden = !this.adding;
    this.addBtn.hidden = !this.edit || this.adding;
    if (this.adding) this.input.focus();
  }

  private commit(): void {
    const v = this.input.value.trim().replace(/,+$/, '');
    if (v && !this.tags.some((x) => x.toLowerCase() === v.toLowerCase()) && this.tags.length < 30) {
      this.tags.push(v.slice(0, 50));
      this.onChange?.();
    }
    this.input.value = '';
    this.render();
  }

  get(): unknown {
    return [...this.tags];
  }

  set(v: unknown): void {
    this.tags = Array.isArray(v) ? v.map(String).filter(Boolean) : [];
    this.render();
  }

  setEdit(edit: boolean): void {
    this.edit = edit;
    this.setAdding(false);
    this.render();
  }

  focus(): void {
    if (this.edit) this.setAdding(true);
  }

  private render(): void {
    clear(this.chips);
    if (!this.tags.length && !this.edit) {
      this.chips.appendChild(h('span', { class: 'field-view' }, '—'));
      return;
    }
    for (const tag of this.tags) {
      const chip = h('span', { class: 'badge tag-chip' }, tag);
      const color = this.def.labels ? labelColor(this.catalog, tag) : null;
      if (color) { chip.classList.add('colored'); chip.style.setProperty('--tag', color); } // CSSOM (CSP)
      if (this.edit) {
        const x = h('button', { type: 'button', class: 'tag-x', 'aria-label': t('core.field.remove_row') }, '×');
        x.addEventListener('click', () => {
          this.tags = this.tags.filter((x2) => x2 !== tag);
          this.render();
          this.onChange?.();
        });
        chip.appendChild(x);
      }
      this.chips.appendChild(chip);
    }
  }
}

/** Obrázek: hodnota = id položky úložiště (nebo null); výběr z úložiště, nahrání do složky aplikace, odebrání. */
export class ImageControl implements ValueControl {
  el = h('div', { class: 'image-field' });
  onChange: (() => void) | null = null;
  private id: string | null = null;
  private img = h('img', { class: 'image-preview', alt: '', hidden: true });
  private empty = h('div', { class: 'image-empty' }, icon('image', 'icon big'));
  private pick = h('button', { type: 'button', class: 'btn' }, t('core.field.pick_image'));
  private up = h('button', { type: 'button', class: 'btn' }, t('core.field.upload_image'));
  private remove = h('button', { type: 'button', class: 'btn ghost' }, t('core.field.remove_image'));
  private actions = h('div', { class: 'row image-actions' }, this.pick, this.up, this.remove);

  constructor(private def: FieldDef) {
    this.el.append(h('div', { class: 'image-box' }, this.img, this.empty), this.actions);
    this.pick.addEventListener('click', async () => {
      const picked = await pickFile({ mode: 'storage', accept: ['image/*'], defaultFolder: 'Obrázky' });
      if (picked?.[0]) this.apply(picked[0]);
    });
    this.up.addEventListener('click', async () => {
      const c = await pickNative({ multiple: false, accept: 'image/*' });
      const file = c?.files[0];
      if (!file) return;
      try {
        const folder = this.def.folder;
        let parentId: string;
        if (folder) {
          const r = await core<{ item: StorageItem }>('storage', 'appFolder', { app: folder.app, name: folder.name });
          parentId = r.item.id;
        } else {
          const r = await core<{ item: StorageItem }>('storage', 'folder', { name: 'Obrázky' });
          parentId = r.item.id;
        }
        const res = (await upload(parentId, [file])) as { items?: StorageItem[] };
        if (res.items?.[0]) this.apply(res.items[0]);
      } catch {
        /* chybu ukáže API vrstva */
      }
    });
    this.remove.addEventListener('click', () => {
      this.id = null;
      this.show(null);
      this.onChange?.();
    });
  }

  private apply(item: StorageItem): void {
    this.id = item.id;
    this.show(item);
    this.onChange?.();
  }

  private show(item: StorageItem | null): void {
    if (item?.url) {
      this.img.src = prefix() + item.url + '&w=256';
      this.img.hidden = false;
      this.empty.hidden = true;
    } else {
      this.img.hidden = true;
      this.empty.hidden = false;
    }
    this.remove.hidden = !item;
  }

  get(): unknown {
    return this.id;
  }

  set(v: unknown): void {
    this.id = typeof v === 'string' && v ? v : null;
    if (!this.id) {
      this.show(null);
      return;
    }
    void core<{ item: StorageItem }>('storage', 'get', { id: this.id }).then((r) => this.show(r.item)).catch(() => this.show(null));
  }

  setEdit(edit: boolean): void {
    this.actions.hidden = !edit;
    this.el.classList.toggle('edit', edit);
  }

  focus(): void {
    this.pick.focus();
  }
}

/** Zaškrtávátka (volitelně po skupinách): hodnota = seznam vybraných hodnot. */
export class ChecksControl implements ValueControl {
  el = h('div', { class: 'checks-field' });
  onChange: (() => void) | null = null;
  private selected = new Set<string>();
  private inputs = new Map<string, HTMLInputElement>();
  private edit = false;

  constructor(private def: FieldDef) {
    const groups = new Map<string, { value: string; label: string }[]>();
    for (const o of def.options ?? []) {
      const g = o.group ?? '';
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)?.push({ value: String(o.value), label: o.label });
    }
    for (const [g, opts] of groups) {
      const box = h('div', { class: 'checks-group' });
      if (g) box.appendChild(h('h3', null, tk(g)));
      for (const o of opts) {
        const input = h('input', { type: 'checkbox', value: o.value });
        input.addEventListener('change', () => {
          if (input.checked) this.selected.add(o.value);
          else this.selected.delete(o.value);
          this.onChange?.();
        });
        this.inputs.set(o.value, input);
        box.appendChild(h('label', { class: 'check', dataset: { value: o.value } }, input, h('span', null, tk(o.label))));
      }
      this.el.appendChild(box);
    }
  }

  get(): unknown {
    return Array.from(this.selected);
  }

  set(v: unknown): void {
    this.selected = new Set(Array.isArray(v) ? v.map(String) : []);
    for (const [val, input] of this.inputs) input.checked = this.selected.has(val);
    this.paint();
  }

  setEdit(edit: boolean): void {
    this.edit = edit;
    for (const input of this.inputs.values()) input.disabled = !edit;
    this.paint();
  }

  focus(): void {
    this.inputs.values().next().value?.focus();
  }

  /** V režimu zobrazení ukázat jen zaškrtnuté. */
  private paint(): void {
    this.el.classList.toggle('view', !this.edit);
    this.el.querySelectorAll<HTMLElement>('label.check').forEach((l) => { l.hidden = !this.edit && !this.selected.has(l.dataset['value'] ?? ''); });
    this.el.querySelectorAll<HTMLElement>('.checks-group').forEach((g) => { g.hidden = !this.edit && !Array.from(g.querySelectorAll<HTMLElement>('label.check')).some((l) => !l.hidden); });
  }
}

/** Výchozí vzorník (stejné odstíny jako v Kalendáři: čitelné v obou motivech). */
export const COLOR_PALETTE = ['#1f6fd0', '#0e8a8a', '#1f8a4c', '#7a9a01', '#d97b00', '#d6336c', '#b2123f', '#6d3fc0', '#5b6b7a', '#8a5a2b'];

/** Barva: vzorník + vlastní barva přes systémový výběr; hodnota '#rrggbb'. */
export class ColorControl implements ValueControl {
  el = h('div', { class: 'color-field' });
  onChange: (() => void) | null = null;
  private value = '';
  private swatches = h('div', { class: 'color-swatches' });
  private custom = h('input', { type: 'color', class: 'color-custom', title: t('core.field.color_custom') });
  private view = h('span', { class: 'color-view' });

  constructor(def: FieldDef) {
    for (const c of def.suggest?.length ? def.suggest : COLOR_PALETTE) {
      const b = h('button', { type: 'button', class: 'color-swatch', style: { '--c': c }, dataset: { c }, title: c });
      b.addEventListener('click', () => this.pick(c));
      this.swatches.appendChild(b);
    }
    const customWrap = h('label', { class: 'color-swatch custom', title: t('core.field.color_custom') }, this.custom, icon('palette'));
    this.custom.addEventListener('input', () => this.pick(this.custom.value));
    this.swatches.appendChild(customWrap);
    this.el.append(this.swatches, this.view);
  }

  private pick(c: string): void {
    this.value = c.toLowerCase();
    this.paint();
    this.onChange?.();
  }

  get(): unknown {
    return this.value || null;
  }

  set(v: unknown): void {
    this.value = typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v) ? v.toLowerCase() : '';
    this.paint();
  }

  setEdit(edit: boolean): void {
    this.el.classList.toggle('view', !edit);
    this.swatches.hidden = !edit;
    this.view.hidden = edit;
  }

  focus(): void {
    this.swatches.querySelector<HTMLElement>('.color-swatch')?.focus();
  }

  private paint(): void {
    this.swatches.querySelectorAll<HTMLElement>('.color-swatch[data-c]').forEach((s) => s.classList.toggle('selected', s.dataset['c'] === this.value));
    const known = Array.from(this.swatches.querySelectorAll<HTMLElement>('.color-swatch[data-c]')).some((s) => s.dataset['c'] === this.value);
    const custom = this.swatches.querySelector<HTMLElement>('.color-swatch.custom');
    custom?.classList.toggle('selected', Boolean(this.value) && !known);
    if (this.value && !known) custom?.style.setProperty('--c', this.value);
    else custom?.style.removeProperty('--c');
    if (this.value) this.custom.value = this.value;
    this.view.replaceChildren(this.value ? h('span', { class: 'color-dot', style: { '--c': this.value } }) : '—', this.value ? ' ' + this.value : '');
  }
}

export function makeComplex(def: FieldDef): ValueControl | null {
  switch (def.type) {
    case 'select': // volby s barvou (kalendář, adresář): vlastní rozbalovací seznam s tečkami, nativní <select> barvy neumí
      return def.options?.some((o) => o.color) ? new ColorSelectControl(def) : null;
    case 'color':
      return new ColorControl(def);
    case 'checks':
      return new ChecksControl(def);
    case 'multi':
      return new RowsControl(def);
    case 'tags':
      return new TagsControl(def);
    case 'image':
      return new ImageControl(def);
    default:
      return null;
  }
}

/** Výběr jedné volby s barevnou tečkou: tlačítko (tečka + název + šipka) a nabídka IwMenu; v režimu čtení jen tečka + název. */
class ColorSelectControl implements ValueControl {
  readonly el: HTMLElement;
  onChange: (() => void) | null = null;
  private value: string | null = null;
  private edit = false;
  private readonly btn: HTMLButtonElement;
  private readonly view: HTMLElement;

  constructor(private readonly def: FieldDef) {
    this.btn = h('button', { type: 'button', class: 'input color-select-btn', 'aria-haspopup': 'listbox' }) as HTMLButtonElement;
    this.view = h('div', { class: 'field-view color-select-view' });
    this.el = h('div', { class: 'color-select' }, this.btn, this.view);
    this.btn.addEventListener('click', () => this.open());
    this.btn.addEventListener('keydown', (e) => { if (e.key === 'ArrowDown' || e.key === ' ') { e.preventDefault(); this.open(); } });
    const first = def.options?.[0];
    if (def.required && first) this.value = String(first.value);
    this.render();
  }

  private options(): { value: string; label: string; color?: string }[] {
    return (this.def.options ?? []).map((o) => ({ value: String(o.value), label: tk(o.label), color: o.color }));
  }

  private open(): void {
    const items = this.options().map((o) => ({ label: o.label, color: o.color || null, checked: o.value === this.value, onSelect: () => { this.value = o.value; this.render(); this.onChange?.(); this.btn.focus(); } }));
    if (!this.def.required) items.unshift({ label: '—', color: null, checked: this.value === null, onSelect: () => { this.value = null; this.render(); this.onChange?.(); this.btn.focus(); } });
    IwMenu.showAt(items, this.btn);
  }

  private render(): void {
    const cur = this.options().find((o) => o.value === this.value);
    const dot = cur?.color ? h('span', { class: 'cal-dot', style: { background: cur.color } }) : null;
    const colored = (this.def.options ?? []).some((o) => o.color);
    this.btn.replaceChildren(dot ?? (colored ? h('span', { class: 'cal-dot empty' }) : ''), h('span', { class: 'color-select-label' }, cur?.label ?? '—'), icon('chevron', 'icon chevron'));
    this.view.replaceChildren(dot?.cloneNode(true) ?? '', cur?.label ?? '—');
  }

  get(): unknown {
    return this.value ?? (this.def.required ? (this.def.options?.[0]?.value ?? null) : null);
  }

  set(v: unknown): void {
    this.value = v === null || v === undefined || v === '' ? null : String(v);
    this.render();
  }

  setEdit(edit: boolean): void {
    this.edit = edit;
    this.btn.hidden = !edit;
    this.view.hidden = edit;
  }

  focus(): void {
    if (this.edit) this.btn.focus();
  }
}
